import Image from "~/components/ui/Image";
import { useEffect, useState } from "react";
import { sanitizeStoreUrl } from "~/utils/constants";
import type { Brand } from "~/components/Header/types";

export type { Brand };

export interface Props {
  brands: Brand[];
  device: Device;
}

type Device = "MOBILE";

// Altura anterior (48px) espremia os logos (mesmo com logoWidth/logoHeight
// configurados bem maiores no CMS) — faixa lia como "discreta/pequena"
// (item de backlog, ref. visual "OH BOY", mesmo pedido já atendido na faixa
// de marcas desktop em 30/07 — ver Alert.tsx). Aumentada pra dar mais
// respiro vertical aos logos sem reduzir a largura/opacidade já definidas.
const DEVICE_WRAPPER_CLASSES = {
  MOBILE:
    "min-w-[265px] w-[50%] flex flex-row items-center bg-[#F0F0F0] m-0 h-full min-h-[72px] max-h-[72px]",
};

const DEVICE_A_CLASSES = {
  MOBILE: "flex justify-center p-0 px-[10px] text-center",
};

/**
 * O logo da marca principal (Daniela Tombini) é um lettering bem mais largo e
 * com respiro transparente no PNG, então ao dividir o espaço em partes iguais
 * ele acaba renderizando visualmente muito menor que Vivame e Tombini.
 * Dando mais largura para a primeira marca as três assinaturas ficam com a
 * mesma altura aparente.
 */
const MAIN_BRAND_WIDTH_SHARE = 45;

function Brands({ brands = [], device }: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    const savedIndex = sessionStorage.getItem("activeBrandIndex");
    setActiveIndex(savedIndex !== null ? parseInt(savedIndex) : 0);
  }, []);

  const handleClick = (index: number) => {
    setActiveIndex(index);
    sessionStorage.setItem("activeBrandIndex", index.toString());
  };

  const otherBrandsWidthShare = brands.length > 1
    ? (100 - MAIN_BRAND_WIDTH_SHARE) / (brands.length - 1)
    : 100;

  return (
    <div className={`${DEVICE_WRAPPER_CLASSES[device]} brands-header`}>
      {brands &&
        brands.map((brand, index) => {
          const isFirstBrand = index === 0;
          const imageWidth = isFirstBrand ? 130 : 70;
          const imageHeight = isFirstBrand ? 130 : 70;
          const logoSrc = brand?.logo?.src;

          if (!logoSrc) {
            return null;
          }

          return (
            <a
              className={`${DEVICE_A_CLASSES[device]} h-full items-center flex ${
                activeIndex === index
                  ? "opacity-100 brand-active"
                  : "opacity-60"
              }`}
              href={sanitizeStoreUrl(brand?.url)}
              key={brand?.url}
              onClick={() => handleClick(index)}
              style={{
                width: `${
                  isFirstBrand ? MAIN_BRAND_WIDTH_SHARE : otherBrandsWidthShare
                }%`,
              }}
            >
              <Image
                loading="lazy"
                fetchPriority="high"
                src={logoSrc}
                alt={brand?.logo?.alt}
                width={brand?.logoWidth ?? imageWidth}
                height={brand?.logoHeight ?? imageHeight}
                className={`w-full object-scale-down brands-image logo-brands ${
                  isFirstBrand ? "max-w-[160px]" : "max-w-[70px]"
                }`}
              />
            </a>
          );
        })}
    </div>
  );
}

export default Brands;
